import React from 'react';

import ImageLazy from '@common/react/components/UI/ImageLazy/ImageLazy';

import Background from '@app/components/UI/Background/Background';
import Card from '@app/components/UI/Card/Card';
import Footprint from '@app/components/UI/Footprint/Footprint';
import HelmetProvider from '@app/components/UI/Helmet/HelmetCustom';
import Line2 from '@app/components/UI/Lines/Line2';
import SelectedText from '@app/components/UI/SelectedText';

import '@app/scss/pages/GeriatricPets.scss';

enum AgeType {
	Months = 0,
	Years = 1,
}

const PetsAgingCalculator: React.FC = () => {
	const [age, setAge] = React.useState('');
	const [type, setType] = React.useState<AgeType>(AgeType.Years);
	const [showCalculate, setShowCalculate] = React.useState<boolean>(true);

	return (
		<div className="aging-calculator">
			<img className="cuddles-cat" width={318} height={216} src="/images/cuddles-Cat.png" />
			{showCalculate ? (
				<div>
					<h2>Dogs aging calculator</h2>
					<p className="h4">
						Using the Dog Aging Calculator you can compare what your dog’s age is to humane age. Regardless of their age (or breed), our
						geriatric pets are those that are fragile and susceptible to adverse outcomes (e.g. falls, illness) and, therefore, need
						different care in the home as well as the clinic.
					</p>
					<div className="form-group">
						<input
							value={age}
							name="age"
							type="number"
							onChange={(e) => +e.currentTarget.value >= 0 && setAge(e.currentTarget.value)}
							className="form-control"
							placeholder="Age"
						/>
					</div>
					<div className="radio-group">
						<label>
							<input
								name="type"
								type="radio"
								value={AgeType.Months}
								checked={type === AgeType.Months}
								onChange={(e) => setType(AgeType.Months)}
							/>
							<span />
							Months
						</label>
						<label>
							<input
								name="type"
								type="radio"
								value={AgeType.Years}
								checked={type === AgeType.Years}
								onChange={(e) => setType(AgeType.Years)}
							/>
							<span />
							Years
						</label>
					</div>
					<div className="text-center">
						<button type="button" className="btn btn-primary" onClick={() => +age > 0 && setShowCalculate(false)}>
							<Footprint />
							Calculate
						</button>
					</div>
				</div>
			) : (
				<div className="calculated-age">
					<div className="h2 intent-down">
						In human years your
						<br />
						pet is
					</div>
					<div className="h2 intent-down">
						{type === AgeType.Years ? Math.round(Math.log(+age) * 16 + 31) : Math.round((Math.log(+age) * 16) / 12 + 31)}
					</div>
					<div className="h4 intent-down">years old</div>
					<button type="button" className="btn btn-primary" onClick={() => setShowCalculate(true)}>
						<Footprint />
						Calculate again
					</button>
				</div>
			)}
		</div>
	);
};

const GeriatricPets: React.FC = () => {
	return (
		<HelmetProvider title="Geriatric Pets">
			<div className="geriatric-pets-page pages">
				<Background />
				<div className="geriatric-pets-screen">
					<div className="container">
						<Card
							image={
								<>
									<h2>
										<SelectedText text="Geriatric " />
										Pets
									</h2>
									<p className="h4">
										There is nothing better to Dr. Mary than a skinny old cat or wobbly geriatric dog. The elderly pet
										population is a growing demographic and owning a super-senior brings a different set of
										responsibilities, challenges and emotional strains. Dr. Mary has had her fair share of grey muzzles
										throughout her life. From a kidney failure cat to a dog with major mobility issues, and many in
										between. She knows firsthand what struggles families go through when dealing with the aging pet.
									</p>
									<picture className="show-mobile">
										<source media="(max-width: 1100px)" srcSet="/images/GeriatricPets-m.png" />
										<source media="(min-width: 1101px)" srcSet="/images/GeriatricPets.png" />
										<img className="geriatric-pets-screen-img" srcSet="/images/GeriatricPets.png" />
									</picture>
									<PetsAgingCalculator />
								</>
							}
							text={
								<>
									<picture className="mobile-hidden">
										<source media="(max-width: 1100px)" srcSet="/images/GeriatricPets-m.png" />
										<source media="(min-width: 1101px)" srcSet="/images/GeriatricPets.png" />
										<img className="geriatric-pets-screen-img" srcSet="/images/GeriatricPets.png" />
									</picture>
									<h3 className="text-center">
										Educating the entire veterinary staff on caring for the geriatric pet is something Dr.Mary adores! She
										helps hospitals create protocols for standards of care for the geriatric pet, explore ways that
										clinics can educate pet’s parents on the ailments of the aging process as well as find ways to
										encourage them to visit their veterinarian for help.
									</h3>
									<ImageLazy src="images/img111.png" width={461} height={354} alt="dog" />
								</>
							}
						/>
					</div>
				</div>
				<div className="bg-white-opacity">
					<div className="container">
						<div className="geriatric-pets-last-screen">
							<Card
								image={<ImageLazy src="/images/cat111.png" width={688} height={468} alt="cat" />}
								text={
									<>
										<h2>
											Why the love of
											<SelectedText text=" old animals" line={<Line2 />} />
										</h2>
										<p className="h4">
											Approximately 50% of geriatric pets are never seen by their primary care veterinarian – and that
											is a statistic she would like to drastically change. She also empathizes with the families in
											their physical, emotional and time struggles during this life phase. Because of this, it has
											become her purpose to focus on the science of aging, the management of the elderly, the medical
											care veterinarians provide as well as the caregiver burden.
										</p>
									</>
								}
							/>
						</div>
					</div>
				</div>
			</div>
		</HelmetProvider>
	);
};

export default GeriatricPets;
